import { File, LayoutGrid, Palette, LayoutPanelLeft } from "lucide-react";

import { cn } from "@/lib/utils";

interface MentionItemProps {
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
}

// ============ File Mention Item ============

interface FileMentionItemProps extends MentionItemProps {
  name: string;
  path?: string;
}

export function FileMentionItem({
  name,
  isSelected,
  onSelect,
  onHover,
}: FileMentionItemProps) {
  // Split path: "src/components/Button.tsx" → fileName="Button.tsx", folder="src/components"
  const lastSlash = name.lastIndexOf("/");
  const fileName = lastSlash >= 0 ? name.substring(lastSlash + 1) : name;
  const folder = lastSlash >= 0 ? name.substring(0, lastSlash) : "";

  return (
    <button
      type="button"
      className={cn(
        "w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-accent/50",
        isSelected && "bg-accent",
      )}
      onClick={onSelect}
      onMouseEnter={onHover}
    >
      <File className="h-4 w-4 text-blue-400 flex-shrink-0" />
      <span className="truncate">{fileName}</span>
      {folder && (
        <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
          {folder}
        </span>
      )}
    </button>
  );
}

// ============ Project Mention Item ============

interface ProjectMentionItemProps extends MentionItemProps {
  name: string;
  description?: string | null;
  status?: string | null;
}

export function ProjectMentionItem({
  name,
  description,
  status,
  isSelected,
  onSelect,
  onHover,
}: ProjectMentionItemProps) {
  return (
    <button
      type="button"
      className={cn(
        "w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent/50",
        isSelected && "bg-accent",
      )}
      onClick={onSelect}
      onMouseEnter={onHover}
    >
      <LayoutGrid className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      <span className="truncate max-w-[140px]">{name}</span>
      {description && (
        <span className="text-xs text-muted-foreground truncate">
          {description}
        </span>
      )}
      {status && (
        <span
          className={cn(
            "ml-auto w-2 h-2 rounded-full flex-shrink-0",
            status === "running" && "bg-green-500",
            status === "paused" && "bg-yellow-500",
            !["running", "paused"].includes(status) && "bg-gray-400",
          )}
        />
      )}
    </button>
  );
}

// ============ Theme Mention Item ============

interface ThemeMentionItemProps extends MentionItemProps {
  name: string;
}

export function ThemeMentionItem({
  name,
  isSelected,
  onSelect,
  onHover,
}: ThemeMentionItemProps) {
  return (
    <button
      type="button"
      className={cn(
        "w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent/50",
        isSelected && "bg-accent",
      )}
      onClick={onSelect}
      onMouseEnter={onHover}
    >
      <Palette className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      <span className="truncate">{name}</span>
    </button>
  );
}

// ============ Skill Mention Item ============

interface SkillMentionItemProps extends MentionItemProps {
  name: string;
  description?: string;
}

export function SkillMentionItem({
  name,
  description,
  isSelected,
  onSelect,
  onHover,
}: SkillMentionItemProps) {
  return (
    <button
      type="button"
      className={cn(
        "w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent/50",
        isSelected && "bg-accent",
      )}
      onClick={onSelect}
      onMouseEnter={onHover}
    >
      <LayoutPanelLeft className="h-4 w-4 text-purple-500 flex-shrink-0" />
      <span className="truncate">{name}</span>
      {description && (
        <span className="ml-auto text-xs text-muted-foreground truncate max-w-[140px]">
          {description}
        </span>
      )}
    </button>
  );
}
